import { createAction } from 'redux-actions';
import SwaggerAPI from 'data/SwaggerAPI';
import _ from 'lodash';
import { createSelector } from 'reselect';

const REQUEST_USERS = 'users/REQUEST_USERS';
const RECEIVE_USERS = 'users/RECEIVE_USERS';
const SET_USER_INDEX = 'users/SET_USER_INDEX';
const SET_PAGE_SIZE = 'users/SET_PAGE_SIZE';
const SET_USER_ORG_NAME = 'users/SET_USER_ORG_NAME';
const SELECT_USER = 'users/SELECT_USER';
const CREATE_USER = 'users/CREATE_USER';
const UPDATE_USER = 'users/UPDATE_USER';
const DELETE_USER = 'users/DELETE_USER';
const USER_RESET_PASSWORD = 'users/USER_RESET_PASSWORD';
const INVITE_USER = 'users/USER_INVITE';
const REGISTER_USER = 'users/USER_REGISTER';
const SET_FILTER_BY_ORG = 'users/SET_FILTER_BY_ORG';
const CLEAR_ERROR = 'users/CLEAR_ERROR';

const initialState = {
  isFetching: false,
  selectedUser: undefined,
  users: undefined,
  search: undefined,
  pageSize: 10,
  page: 0,
  startIndex: 0,
  userOrgName: undefined,
  filterBy: 'all_users',
  error: undefined,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case REQUEST_USERS:
      return {
        ...state,
        isFetching: true,
      };
    case RECEIVE_USERS: {
      return {
        ...state,
        isFetching: false,
        users: action.payload,
      };
    }
    case CLEAR_ERROR: {
      return {
        ...state,
        error: undefined,
      };
    }
    case INVITE_USER: {
      if (action.error) {
        const errorResponse = {
          message: action?.payload?.response?.body?.message,
          statusCode: action?.payload?.statusCode,
        };
        return {
          ...state,
          isFetching: false,
          error: errorResponse,
        };
      }
      return {
        ...state,
        isFetching: false,
      };
    }
    case REGISTER_USER: {
      if (action.error) {
        const errorResponse = {
          message: action?.payload?.response?.body?.message,
          statusCode: action?.payload?.statusCode,
        };
        return {
          ...state,
          isFetching: false,
          error: errorResponse,
        };
      }
      return {
        ...state,
        isFetching: false,
      };
    }
    case UPDATE_USER: {
      if (action.payload) {
        const tempUserList = _.clone(state.users);
        const modifiedIndex = _.findIndex(tempUserList, { id: action.payload?.id });

        if (modifiedIndex > -1) {
          tempUserList.splice(modifiedIndex, 1, action.payload);
          return { ...state, users: tempUserList, isFetching: false };
        }
      }
      return { ...state, isFetching: false };
    }
    case SET_USER_INDEX: {
      const startIndex = action.page * state.pageSize;
      return {
        ...state,
        page: action.page,
        startIndex: startIndex,
      };
    }
    case SET_PAGE_SIZE: {
      const startIndex = 0;
      const page = 0;
      let pageSize = state.pageSize;
      if (action.pageSize) {
        pageSize = action.pageSize;
      }
      return {
        ...state,
        pageSize: pageSize,
        startIndex: startIndex,
        page: page,
      };
    }
    case SET_USER_ORG_NAME: {
      return {
        ...state,
        userOrgName: action.name,
      };
    }
    case SELECT_USER: {
      return {
        ...state,
        selectedUser: action.payload,
      };
    }
    case SET_FILTER_BY_ORG:
      return {
        ...state,
        filterBy: action.filter,
      };
    case CREATE_USER: {
      const newUsers = [];
      _.forEach(state.users, (user) => {
        newUsers.push(user);
      });
      newUsers.push(action.payload);
      return {
        ...state,
        users: newUsers,
      };
    }
    case USER_RESET_PASSWORD: {
      if (!action.payload) return state;
      if (action.error) {
        const errorResponse = {
          message: action?.payload?.response?.body?.message,
          statusCode: action?.payload?.statusCode,
        };
        return {
          ...state,
          isFetching: false,
          error: errorResponse,
        };
      }
      return {
        ...state,
        isFetching: false,
        error: undefined,
      };
    }

    default:
      return state;
  }
};

export const resetUserPassword = async (passwordObj, token) => {
  const response = await SwaggerAPI.userResetPassword(passwordObj, token);
  return response;
};

export const startFetch = () => ({
  type: REQUEST_USERS,
});
export const fetchUsers = createAction(RECEIVE_USERS, SwaggerAPI.listUsers);

export const setSelectedUser = (userId) => ({
  type: SELECT_USER,
  userId,
});

export const userRequestPasswordReset = (email, clientId) => {
  return SwaggerAPI.userRequestPasswordReset(email, clientId);
};

export const userResetPassword = (passwordObj, token) => {
  return SwaggerAPI.userResetPassword(passwordObj, token);
};

export const userInviteRequest = createAction(INVITE_USER, SwaggerAPI.userRegistrationInvite);
export const userCompleteRegistration = createAction(REGISTER_USER, SwaggerAPI.userCompleteRegistration);
export const selectUser = createAction(SELECT_USER, SwaggerAPI.selectUser);
export const createUser = createAction(CREATE_USER, SwaggerAPI.createUser);
export const updateUser = createAction(UPDATE_USER, SwaggerAPI.updateUser);
export const deleteUser = createAction(DELETE_USER, SwaggerAPI.deleteUser);

const UsersSelector = (function () {
  const setUserOrgName = (name) => ({ type: SET_USER_ORG_NAME, name });
  const setFilterByOrg = (filter) => ({ type: SET_FILTER_BY_ORG, filter });
  const clearErrors = () => ({ type: CLEAR_ERROR });
  const getSelectedUser = (state) => state.users.selectedUser;
  const getUsersPage = (state) => state.users.users;
  const getUserOrgName = (state) => state.users.userOrgName;
  const getFilterByOrg = (state) => state.users.filterBy;
  const getIsFetching = (state) => state.users.isFetching;
  const getError = (state) => state.users.error;
  const getOrganizations = (state) => state.organizations.organizations;

  const filteredUsers = createSelector(
    getFilterByOrg,
    getUsersPage,
    getOrganizations,
    (filter, users, organizations) => {
      if (filter === 'all_users') {
        return users;
      }
      const org = _.find(organizations, { name: filter });
      if (!org) {
        return [];
      } else {
        return _.filter(users, (user) => user.organization_id === org.id);
      }
    }
  );

  return {
    setUserOrgName,
    setFilterByOrg,
    getSelectedUser,
    getUsersPage,
    getUserOrgName,
    getFilterByOrg,
    getIsFetching,
    clearErrors,
    getError,
    filteredUsers,
  };
})();

export default UsersSelector;
